import React, { useEffect, useState } from 'react';
import { Modal, Spinner, Table } from 'react-bootstrap';
import ApiService from '../../../_services/ApiService';

interface ViewAttendanceModalProps {
  show: boolean;
  activitySlotId: number | null;
  onClose: () => void;
}

interface AttendanceHeader {
  topicName: string;
  instructorName: string;
  instructorCode: string;
  date: string;
  startTime: string;
  endTime: string;
  presentCount: number;
  absentCount: number;
  notMarkedCount: number;
  totalCount: number;
  percentage: number;
}

interface AttendanceRow {
  serialNo: number;
  enrollmentId: number;
  studentCode: string;
  name: string;
  batchName: string;
  attendance: number | null;
  remarks: string;
}

interface AttendanceResponse {
  header: AttendanceHeader;
  students: AttendanceRow[];
}

const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const formatDate = (raw: string): string => {
  if (!raw) return '';
  // Backend may return either a plain "YYYY-MM-DD" or a full ISO timestamp
  // ("YYYY-MM-DDTHH:mm:ss.sssZ"). Strip the time portion and parse the date
  // parts directly to avoid timezone shifts.
  const datePart = raw.split('T')[0];
  const [y, m, d] = (datePart || '').split('-').map(Number);
  if (!y || !m || !d) return raw;
  const monthName = MONTH_NAMES[m - 1] ?? '';
  return `${String(d).padStart(2, '0')} ${monthName} ${y}`;
};

const formatTime = (raw: string): string => {
  if (!raw) return '';
  const parts = raw.split(':');
  if (parts.length < 2) return raw;
  const h = parseInt(parts[0], 10);
  const m = parts[1];
  const ampm = h >= 12 ? 'pm' : 'am';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${m} ${ampm}`;
};

const renderAttendance = (value: number | null): React.ReactNode => {
  if (value === 0) return <span style={{ color: '#2e7d32', fontWeight: 600 }}>P</span>;
  if (value === 1) return <span style={{ color: '#c62828', fontWeight: 600 }}>A</span>;
  return <span style={{ color: '#9e9e9e' }}>-</span>;
};

const ViewAttendanceModal: React.FC<ViewAttendanceModalProps> = ({
  show,
  activitySlotId,
  onClose,
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<AttendanceResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!show || activitySlotId === null) {
      setData(null);
      setError(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setData(null);
    ApiService.getActivitySlotAttendanceView(activitySlotId)
      .then((res) => {
        if (cancelled) return;
        const payload = res?.data?.data ?? res?.data;
        if (!payload || !payload.header || !payload.students) {
          setError('Unexpected response from server.');
        } else {
          setData(payload as AttendanceResponse);
        }
      })
      .catch((err: any) => {
        if (cancelled) return;
        setError(
          err?.response?.data?.message ||
            err?.message ||
            'Failed to load attendance.',
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [show, activitySlotId]);

  const header = data?.header;
  const students = data?.students ?? [];

  return (
    <Modal show={show} onHide={onClose} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>View Attendance</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <Spinner animation="border" role="status" size="sm" /> Loading...
          </div>
        )}
        {error && !loading && (
          <div style={{ color: '#c62828', padding: '12px 0' }}>{error}</div>
        )}
        {header && !loading && !error && (
          <>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                rowGap: 6,
                columnGap: 24,
                marginBottom: 16,
                fontSize: 13,
              }}
            >
              <div>
                <strong>Topic :</strong> {header.topicName || '-'}
              </div>
              <div>
                <strong>Instructor :</strong>{' '}
                {header.instructorName || '-'}
                {header.instructorCode ? ` [ ${header.instructorCode} ]` : ''}
              </div>
              <div>
                <strong>Date :</strong> {formatDate(header.date)}{' '}
                {formatTime(header.startTime)}-{formatTime(header.endTime)}
              </div>
              <div>
                <strong>Attendance :</strong> {header.presentCount}/
                {header.totalCount} [{header.percentage}%]
              </div>
            </div>
            <Table striped bordered hover size="sm" responsive>
              <thead>
                <tr>
                  <th style={{ width: 50 }}>S.No</th>
                  <th>Student Code</th>
                  <th>Student Name</th>
                  <th>Batch</th>
                  <th style={{ width: 90 }}>Attendance</th>
                  <th>Conversation</th>
                </tr>
              </thead>
              <tbody>
                {students.length === 0 && (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', color: '#9e9e9e' }}>
                      No students assigned to this slot.
                    </td>
                  </tr>
                )}
                {students.map((row) => (
                  <tr key={row.enrollmentId}>
                    <td>{row.serialNo}</td>
                    <td>{row.studentCode}</td>
                    <td>{row.name}</td>
                    <td>{row.batchName}</td>
                    <td>{renderAttendance(row.attendance)}</td>
                    <td>{row.remarks}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default ViewAttendanceModal;
